import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { MultipleImageUploader } from '@shared/components';
import { MobileCropper } from '@shared/components/ImageCropper';

import { ImagesSectionProps } from '../types';
import styles from './images-section.module.scss';

const ImagesSection: FC<ImagesSectionProps> = ({
  control,
  errors,
  isReadOnly,
  cropDataRef,
  images,
  gameType = 'memory',
  onUploadMultipleImages,
  showMultipleUpload = false,
  gameDefaults,

}) => {
  const { t } = useTranslation();

  // Определяем перевод для заголовка секции в зависимости от типа игры
  const getSectionTitle = () => {
    switch (gameType) {
      case 'miner':
        return t('clientMiner.imageSectionTitle');
      case 'maze':
        return t('clientMaze.imageSectionTitle');
      case 'slot':
        return t('clientSlot.imageSectionTitle');
      case 'capybara':
        return t('clientCapybara.imageSectionTitle');
      case 'flappy':
        return 'Изображение птички';
      default:
        return t('clientMemory.imageSectionTitle');
    }
  };

  return (
    <Box className={styles.formSection}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h5" className={styles.sectionTitle}>
          {getSectionTitle()}
        </Typography>
        <Tooltip title={t('clientAll.imageUploadTooltip')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
          <IconButton 
            size="small" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '4px',
              '& .MuiSvgIcon-root': {
                fontSize: '0.875rem'
              }
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Аккордеон с примером изображений карточек */}
      {gameDefaults?.tilesExampleImage && (
        <Box sx={{ mb: 2 }}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="example-tiles-image-content"
              id="example-tiles-image-header"
              sx={{
                '& .MuiAccordionSummary-content': {
                  justifyContent: 'center'
                }
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                <VisibilityIcon fontSize="small" />
                {t('clientMemory.exampleTilesImage') || 'Посмотреть пример карточек'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img
                  src={Array.isArray(gameDefaults.tilesExampleImage) 
                    ? gameDefaults.tilesExampleImage[0] 
                    : gameDefaults.tilesExampleImage}
                  alt="Пример изображений карточек"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                  onError={(e) => {
                    console.error('Failed to load tiles example image:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      
      {/* Множественная загрузка изображений */}
      {showMultipleUpload && onUploadMultipleImages && (
        <MultipleImageUploader
          onUploadMultipleImages={onUploadMultipleImages}
          isDisabled={isReadOnly}
          title={t('clientMemory.selectMultipleImages')}
          buttonText={t('clientMemory.uploadMultiple')}
        />
      )}
      
      <Grid container spacing={3}>
        {/* Якорь для прокрутки к первой ошибке изображений */}
        <Grid item xs={12} id="form-errors" style={{ position: 'relative' }} />
        {images?.map((imageConfig) => (
          <Grid item key={imageConfig.name} {...(imageConfig.gridProps || { xs: 12, md: 6 })}>
            <Controller
              name={imageConfig.name}
              control={control}
              rules={{ required: (imageConfig as any).required ? t('clientAll.validation.required') : false }}
              render={({ field }) => {
                const fieldError = imageConfig.name
                  .split('.')
                  .reduce<any>((acc, key) => (acc ? acc[key] : undefined), errors);
                return (
                  <Box sx={{ position: 'relative' }}>
                    <MobileCropper
                      label={t(imageConfig.label)}
                      value={field.value}
                      onChange={field.onChange}
                      aspectRatio={imageConfig.aspectRatio}
                      isDisabled={isReadOnly}
                      maskImage={imageConfig.maskImage || ''}
                      toolTipContent={imageConfig.tooltip ? t(imageConfig.tooltip) : ''}
                      fieldName={imageConfig.name}
                      onCroppedPixelsChange={(fieldName, croppedPixels) => {
                        cropDataRef.current[fieldName] = croppedPixels;
                      }}
                      hasError={!!fieldError}
                      errorMessage={(fieldError?.message as string) || ''}
                      gameType={gameType}
              
      
                    />
                  </Box>
                );
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ImagesSection; 